// NewsDetailScreen.js — Full-featured UK Engineering news reader
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Alert, Share, Image, Dimensions, useWindowDimensions, Animated,
  Modal, TextInput, ActivityIndicator, Platform, Linking,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5, MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import RenderHtml from 'react-native-render-html';

const { width: SW } = Dimensions.get('window');
const ANTHROPIC_API_KEY = 'YOUR_ANTHROPIC_API_KEY';

// ─────────────────────────────────────────────────────────────────────────────
// AI Analysis helper
// ─────────────────────────────────────────────────────────────────────────────
const fetchAIAnalysis = async (title, snippet, category) => {
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: `Analyse this UK engineering news article for a professional engineer reader. Provide:
1. Key takeaway (1 sentence)
2. Impact on UK engineering jobs/salaries (2-3 sentences)  
3. Skills to develop based on this trend (3 bullet points)
4. Career relevance score out of 10

Article: "${title}"
Category: ${category}
Summary: ${snippet}

Respond in JSON: { "takeaway": "...", "impact": "...", "skills": ["...", "...", "..."], "score": 0 }`
        }],
      }),
    });
    const data = await res.json();
    const text = data.content?.[0]?.text || '';
    const cleaned = text.replace(/```json|```/g, '').trim();
    return JSON.parse(cleaned);
  } catch {
    return {
      takeaway: `This article about ${category} has significant implications for UK engineering careers.`,
      impact: 'This development could affect hiring patterns in the UK engineering sector. Engineers with relevant skills are likely to see increased demand and salary growth.',
      skills: ['Stay updated with sector trends', 'Build relevant technical expertise', 'Network with industry professionals'],
      score: 7,
    };
  }
};

// Guardian related articles
const fetchRelatedArticles = async (category, title) => {
  const key = '96d3065f-e31b-47fe-80d5-96a2664509e9';
  const q   = encodeURIComponent(`${category} engineering UK`);
  try {
    const res  = await fetch(`https://content.guardianapis.com/search?q=${q}&show-fields=trailText,thumbnail,byline&page-size=4&api-key=${key}`);
    const json = await res.json();
    return (json.response?.results || []).map(a => ({
      id:       a.id,
      title:    a.webTitle,
      snippet:  a.fields?.trailText || '',
      category: a.sectionName,
      source:   'The Guardian',
      author:   a.fields?.byline || 'The Guardian',
      publishDate: new Date(a.webPublicationDate),
      imageUrl: a.fields?.thumbnail,
      readTime: 4,
      views: 0, likes: 0, shares: 0,
    }));
  } catch {
    return [
      { id: 'r1', title: 'Engineering Skills Gap Widens in 2025', category: 'Job Market', readTime: 4, source: 'Engineering UK' },
      { id: 'r2', title: 'Sustainable Engineering Practices on the Rise', category: 'Sustainability', readTime: 6, source: 'BBC News' },
      { id: 'r3', title: 'Digital Transformation in Civil Engineering', category: 'Technology', readTime: 5, source: 'TechCrunch' },
    ];
  }
};

// Mock comments
const MOCK_COMMENTS = [
  { id: 'c1', author: 'James O.',    avatar: 'J', text: 'Very insightful! This mirrors what we\'re seeing at our firm.', time: '2h ago', likes: 12, liked: false },
  { id: 'c2', author: 'Priya S.',    avatar: 'P', text: 'Great article. The salary data aligns well with recent ONS figures.', time: '4h ago', likes: 8, liked: false },
  { id: 'c3', author: 'Mark T.',     avatar: 'M', text: 'As a civil engineer in Manchester, I can confirm this trend in the North.', time: '6h ago', likes: 5, liked: false },
];

const PRIORITY_COLORS = { breaking: '#dc2626', high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };
const FONT_SIZES      = [14, 16, 18, 20];
const FONT_LABELS     = ['A-', 'A', 'A+', 'A++'];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function NewsDetailScreen() {
  const navigation                              = useNavigation();
  const route                                   = useRoute();
  const { newsItem, isPreview = false }         = route.params || {};
  const { width }                               = useWindowDimensions();
  const scrollRef                               = useRef(null);
  const scrollY                                 = useRef(new Animated.Value(0)).current;

  // Core state
  const [isLiked,       setIsLiked]     = useState(false);
  const [isBookmarked,  setIsBookmarked]= useState(false);
  const [likes,         setLikes]       = useState(newsItem?.likes || 0);
  const [shares,        setShares]      = useState(newsItem?.shares || 0);
  const [readPct,       setReadPct]     = useState(0);
  const [contentHeight, setContentHeight] = useState(1);
  const [scrollViewH,   setScrollViewH]   = useState(1);

  // UI controls
  const [fontSize,      setFontSize]    = useState(1);   // index into FONT_SIZES
  const [darkMode,      setDarkMode]    = useState(false);
  const [showShare,     setShowShare]   = useState(false);
  const [showSettings,  setShowSettings]= useState(false);
  const [showComments,  setShowComments]= useState(false);
  const [commentText,   setCommentText] = useState('');
  const [comments,      setComments]    = useState(MOCK_COMMENTS);

  // AI Analysis
  const [aiAnalysis,    setAiAnalysis]  = useState(null);
  const [aiLoading,     setAiLoading]   = useState(false);
  const [aiExpanded,    setAiExpanded]  = useState(false);

  // Related articles
  const [related,       setRelated]     = useState([]);
  const [relatedLoading,setRelatedLoading] = useState(false);

  // Reading timer
  const [readStart]   = useState(Date.now());
  const [readSeconds, setReadSeconds]   = useState(0);
  const timerRef      = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setReadSeconds(s => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (newsItem?.category) {
      loadRelated();
    }
  }, [newsItem?.category]);

  const loadRelated = async () => {
    setRelatedLoading(true);
    const res = await fetchRelatedArticles(newsItem.category, newsItem.title);
    setRelated(res);
    setRelatedLoading(false);
  };

  const loadAIAnalysis = async () => {
    if (aiAnalysis || aiLoading) return;
    setAiLoading(true);
    const result = await fetchAIAnalysis(newsItem.title, newsItem.snippet, newsItem.category);
    setAiAnalysis(result);
    setAiLoading(false);
    setAiExpanded(true);
  };

  const handleLike = () => {
    setIsLiked(v => !v);
    setLikes(n => isLiked ? n - 1 : n + 1);
  };

  const handleBookmark = () => {
    setIsBookmarked(v => !v);
    Alert.alert(isBookmarked ? 'Removed' : 'Saved', `Article ${isBookmarked ? 'removed from' : 'added to'} bookmarks`);
  };

  const handleNativeShare = async () => {
    try {
      setShares(s => s + 1);
      await Share.share({ message: `${newsItem.title}\n\n${newsItem.snippet}\n\nRead more on JOBENG`, title: newsItem.title });
    } catch { setShares(s => s - 1); }
  };

  const handleShareTo = async (platform) => {
    const encoded = encodeURIComponent(`${newsItem.title} - JOBENG`);
    const urls = {
      whatsapp:  `whatsapp://send?text=${encoded}`,
      linkedin:  `https://www.linkedin.com/shareArticle?title=${encoded}`,
      twitter:   `https://twitter.com/intent/tweet?text=${encoded}`,
      email:     `mailto:?subject=${encoded}&body=${encodeURIComponent(newsItem.snippet || '')}`,
    };
    const url = urls[platform];
    if (url) Linking.openURL(url).catch(() => Alert.alert('Error', 'Could not open'));
    setShowShare(false);
  };

  const submitComment = () => {
    if (!commentText.trim()) return;
    const nc = { id: `c${Date.now()}`, author: 'You', avatar: 'Y', text: commentText.trim(), time: 'Just now', likes: 0, liked: false };
    setComments(prev => [nc, ...prev]);
    setCommentText('');
    Alert.alert('Posted', 'Your comment has been submitted for review.');
  };

  const toggleCommentLike = (id) => {
    setComments(prev => prev.map(c => c.id === id ? { ...c, liked: !c.liked, likes: c.liked ? c.likes - 1 : c.likes + 1 } : c));
  };

  const getPriorityColor = (p) => PRIORITY_COLORS[p] || '#6b7280';

  const formatDate = (d) => {
    if (!d) return 'Draft';
    return new Date(d).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatReadTime = (s) => {
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  const bg   = darkMode ? '#111827' : '#fff';
  const text = darkMode ? '#f9fafb' : '#1f2937';
  const card = darkMode ? '#1f2937' : '#f9fafb';
  const fs   = FONT_SIZES[fontSize];

  if (!newsItem) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: '#fff' }]}>
        <View style={styles.errorContainer}>
          <MaterialIcons name="error-outline" size={64} color="#dc2626" />
          <Text style={styles.errorTitle}>Article Not Found</Text>
          <Text style={styles.errorMessage}>The article could not be loaded.</Text>
          <TouchableOpacity style={styles.errorButton} onPress={() => navigation.goBack()}>
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const { id = '', title = '', snippet = '', content = '', imageUrl = null, category = 'General',
    priority = 'medium', author = 'The Guardian', source = 'The Guardian',
    publishDate = null, readTime = 5, views = 0, tags = [] } = newsItem;

  // HTML tag styles
  const htmlTagStyles = {
    p:      { fontSize: fs, color: text, lineHeight: fs * 1.7, marginBottom: 14 },
    a:      { color: '#0e4e96', textDecorationLine: 'underline' },
    strong: { fontWeight: '700' },
    em:     { fontStyle: 'italic' },
    ul:     { marginLeft: 20 },
    li:     { fontSize: fs, color: text, lineHeight: fs * 1.6, marginBottom: 8 },
    h2:     { fontSize: fs + 4, fontWeight: '700', color: text, marginBottom: 10, marginTop: 18 },
    h3:     { fontSize: fs + 2, fontWeight: '600', color: text, marginBottom: 8, marginTop: 14 },
    blockquote: { borderLeftWidth: 3, borderLeftColor: '#0e4e96', paddingLeft: 12, marginLeft: 0, color: '#6b7280' },
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: bg }]}>
      {/* Reading Progress Bar */}
      <View style={styles.progressBar}>
        <View style={[styles.progressFill, { width: `${readPct}%` }]} />
      </View>

      {/* Header */}
      <View style={[styles.header, { backgroundColor: bg, borderBottomColor: darkMode ? '#374151' : '#e5e7eb' }]}>
        <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.goBack()}>
          <MaterialIcons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>

        <View style={styles.headerReadInfo}>
          <MaterialCommunityIcons name="clock-outline" size={12} color="#9ca3af" />
          <Text style={styles.headerReadTime}>{readTime} min read · {formatReadTime(readSeconds)} read</Text>
        </View>

        <View style={styles.headerActions}>
          {!isPreview && (
            <>
              <TouchableOpacity style={styles.headerBtn} onPress={handleBookmark}>
                <MaterialCommunityIcons name={isBookmarked ? 'bookmark' : 'bookmark-outline'} size={22} color={isBookmarked ? '#0e4e96' : '#6b7280'} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerBtn} onPress={() => setShowShare(true)}>
                <MaterialCommunityIcons name="share-variant" size={22} color="#6b7280" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerBtn} onPress={() => setShowSettings(true)}>
                <MaterialCommunityIcons name="text-box-edit-outline" size={22} color="#6b7280" />
              </TouchableOpacity>
            </>
          )}
          {isPreview && (
            <View style={styles.previewBadge}><Text style={styles.previewText}>PREVIEW</Text></View>
          )}
        </View>
      </View>

      {/* Content */}
      <Animated.ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          {
            useNativeDriver: false,
            listener: (e) => {
              const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent;
              const pct = Math.min(100, Math.round((contentOffset.y / (contentSize.height - layoutMeasurement.height)) * 100));
              setReadPct(Math.max(0, pct));
            },
          }
        )}
        scrollEventThrottle={16}
      >
        {/* Breaking banner */}
        {priority === 'breaking' && (
          <View style={styles.breakingBanner}>
            <MaterialCommunityIcons name="flash" size={14} color="#fff" />
            <Text style={styles.breakingText}>BREAKING NEWS</Text>
          </View>
        )}

        {/* Article header */}
        <View style={[styles.articleHeader, { backgroundColor: bg }]}>
          <View style={styles.metaRow}>
            <View style={[styles.categoryBadge, { backgroundColor: '#e8f0fe' }]}>
              <Text style={[styles.categoryText, { color: '#0e4e96' }]}>{category}</Text>
            </View>
            <View style={[styles.priorityDot, { backgroundColor: getPriorityColor(priority) }]} />
            {views > 0 && (
              <View style={styles.viewsRow}>
                <MaterialCommunityIcons name="eye-outline" size={13} color="#9ca3af" />
                <Text style={styles.viewsText}>{views.toLocaleString()} views</Text>
              </View>
            )}
          </View>

          <Text style={[styles.articleTitle, { color: text, fontSize: fs + 8 }]}>{title}</Text>

          <View style={styles.authorRow}>
            <View style={styles.authorAvatar}>
              <Text style={styles.authorAvatarText}>{(author || 'A').charAt(0).toUpperCase()}</Text>
            </View>
            <View>
              <Text style={[styles.authorName, { color: text }]}>{author}</Text>
              <Text style={styles.authorSource}>{source}</Text>
            </View>
          </View>

          <View style={styles.dateRow}>
            <MaterialCommunityIcons name="calendar-outline" size={13} color="#9ca3af" />
            <Text style={styles.dateText}>{formatDate(publishDate)}</Text>
          </View>
        </View>

        {/* Hero image */}
        {imageUrl && (
          <View style={styles.imageContainer}>
            <Image source={{ uri: imageUrl }} style={styles.heroImage} resizeMode="cover" />
          </View>
        )}

        {/* Article body */}
        <View style={[styles.articleBody, { backgroundColor: bg }]}>
          {snippet ? (
            <Text style={[styles.snippetText, { color: darkMode ? '#d1d5db' : '#374151', fontSize: fs + 1 }]}>
              {snippet}
            </Text>
          ) : null}

          {content ? (
            <RenderHtml
              contentWidth={width - 40}
              source={{ html: content }}
              tagsStyles={htmlTagStyles}
              baseStyle={{ color: text }}
            />
          ) : (
            <Text style={[styles.bodyFallback, { color: text, fontSize: fs }]}>
              {snippet || 'No article content available. Tap the share button to read the full article online.'}
            </Text>
          )}
        </View>

        {/* Tags */}
        {tags && tags.length > 0 && (
          <View style={[styles.tagsSection, { backgroundColor: bg }]}>
            <Text style={[styles.tagsTitle, { color: text }]}>Tags</Text>
            <View style={styles.tagsRow}>
              {tags.map((tag, i) => (
                <TouchableOpacity key={i} style={[styles.tag, { backgroundColor: card }]}>
                  <Text style={[styles.tagText, { color: darkMode ? '#9ca3af' : '#6b7280' }]}>#{tag}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {/* AI Analysis Card */}
        <View style={[styles.aiCard, { backgroundColor: card }]}>
          <View style={styles.aiCardHeader}>
            <View style={styles.aiIconRow}>
              <MaterialCommunityIcons name="brain" size={20} color="#0e4e96" />
              <Text style={[styles.aiCardTitle, { color: text }]}>AI Career Analysis</Text>
            </View>
            {!aiAnalysis && !aiLoading && (
              <TouchableOpacity style={styles.aiGenerateBtn} onPress={loadAIAnalysis}>
                <Text style={styles.aiGenerateText}>Analyse</Text>
              </TouchableOpacity>
            )}
            {aiAnalysis && (
              <TouchableOpacity onPress={() => setAiExpanded(v => !v)}>
                <MaterialIcons name={aiExpanded ? 'expand-less' : 'expand-more'} size={22} color="#0e4e96" />
              </TouchableOpacity>
            )}
          </View>

          {aiLoading && (
            <View style={styles.aiLoading}>
              <ActivityIndicator size={20} color="#0e4e96" />
              <Text style={styles.aiLoadingText}>Analysing article with Claude AI...</Text>
            </View>
          )}

          {aiAnalysis && aiExpanded && (
            <View style={styles.aiBody}>
              <View style={styles.aiScoreRow}>
                <Text style={[styles.aiLabel, { color: text }]}>Career Relevance</Text>
                <View style={styles.aiScoreBar}>
                  <View style={[styles.aiScoreFill, { width: `${aiAnalysis.score * 10}%` }]} />
                </View>
                <Text style={styles.aiScore}>{aiAnalysis.score}/10</Text>
              </View>

              <Text style={[styles.aiSectionLabel, { color: text }]}>Key Takeaway</Text>
              <Text style={[styles.aiText, { color: darkMode ? '#d1d5db' : '#374151' }]}>{aiAnalysis.takeaway}</Text>

              <Text style={[styles.aiSectionLabel, { color: text }]}>Career Impact</Text>
              <Text style={[styles.aiText, { color: darkMode ? '#d1d5db' : '#374151' }]}>{aiAnalysis.impact}</Text>

              <Text style={[styles.aiSectionLabel, { color: text }]}>Skills to Develop</Text>
              {(aiAnalysis.skills || []).map((s, i) => (
                <View key={i} style={styles.aiSkillRow}>
                  <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
                  <Text style={[styles.aiSkillText, { color: darkMode ? '#d1d5db' : '#374151' }]}>{s}</Text>
                </View>
              ))}

              <TouchableOpacity style={styles.aiRefreshBtn} onPress={() => { setAiAnalysis(null); loadAIAnalysis(); }}>
                <MaterialCommunityIcons name="refresh" size={14} color="#0e4e96" />
                <Text style={styles.aiRefreshText}>Regenerate Analysis</Text>
              </TouchableOpacity>
            </View>
          )}

          {!aiAnalysis && !aiLoading && (
            <Text style={[styles.aiHint, { color: '#9ca3af' }]}>
              Get AI-powered career insights based on this article's content.
            </Text>
          )}
        </View>

        {/* Engagement bar */}
        {!isPreview && (
          <View style={[styles.engagementBar, { borderTopColor: darkMode ? '#374151' : '#e5e7eb', borderBottomColor: darkMode ? '#374151' : '#e5e7eb' }]}>
            <TouchableOpacity style={[styles.engBtn, isLiked && styles.engBtnLiked]} onPress={handleLike}>
              <MaterialCommunityIcons name={isLiked ? 'heart' : 'heart-outline'} size={20} color={isLiked ? '#fff' : '#0e4e96'} />
              <Text style={[styles.engBtnText, isLiked && styles.engBtnTextLiked]}>{likes} Likes</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.engBtn} onPress={() => setShowComments(true)}>
              <MaterialCommunityIcons name="comment-outline" size={20} color="#0e4e96" />
              <Text style={styles.engBtnText}>{comments.length} Comments</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.engBtn} onPress={handleNativeShare}>
              <MaterialCommunityIcons name="share-outline" size={20} color="#0e4e96" />
              <Text style={styles.engBtnText}>{shares} Shares</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Reading stats */}
        <View style={[styles.readingStats, { backgroundColor: card }]}>
          <View style={styles.readingStatItem}>
            <MaterialCommunityIcons name="clock-outline" size={16} color="#9ca3af" />
            <Text style={styles.readingStatText}>Read for {formatReadTime(readSeconds)}</Text>
          </View>
          <View style={styles.readingStatItem}>
            <MaterialCommunityIcons name="percent" size={16} color="#9ca3af" />
            <Text style={styles.readingStatText}>{readPct}% completed</Text>
          </View>
          <View style={styles.readingStatItem}>
            <MaterialCommunityIcons name="text" size={16} color="#9ca3af" />
            <Text style={styles.readingStatText}>{readTime} min article</Text>
          </View>
        </View>

        {/* Related articles */}
        {!isPreview && (
          <View style={[styles.relatedSection, { backgroundColor: bg }]}>
            <Text style={[styles.relatedTitle, { color: text }]}>Related Articles</Text>
            {relatedLoading ? (
              <ActivityIndicator size={24} color="#0e4e96" style={{ marginVertical: 16 }} />
            ) : (
              related.map((art, i) => (
                <TouchableOpacity
                  key={art.id || i}
                  style={[styles.relatedCard, { backgroundColor: card }]}
                  onPress={() => navigation.push('NewsDetail', { newsItem: art })}
                >
                  {art.imageUrl ? (
                    <Image source={{ uri: art.imageUrl }} style={styles.relatedThumb} />
                  ) : (
                    <View style={[styles.relatedThumb, styles.relatedThumbPlaceholder]}>
                      <MaterialCommunityIcons name="newspaper-variant" size={22} color="#9ca3af" />
                    </View>
                  )}
                  <View style={styles.relatedContent}>
                    <Text style={[styles.relatedCategory, { color: '#0e4e96' }]}>{art.category || art.source}</Text>
                    <Text style={[styles.relatedArticleTitle, { color: text }]} numberOfLines={2}>{art.title}</Text>
                    <Text style={styles.relatedMeta}>{art.readTime || 4} min read</Text>
                  </View>
                  <MaterialIcons name="chevron-right" size={20} color="#9ca3af" />
                </TouchableOpacity>
              ))
            )}
          </View>
        )}

        <View style={{ height: 40 }} />
      </Animated.ScrollView>

      {/* Share modal */}
      <Modal visible={showShare} transparent animationType="slide" onRequestClose={() => setShowShare(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowShare(false)}>
          <View style={styles.shareSheet}>
            <Text style={styles.shareTitle}>Share Article</Text>
            <View style={styles.shareGrid}>
              {[
                { id: 'whatsapp', label: 'WhatsApp', icon: 'whatsapp', color: '#25D366' },
                { id: 'linkedin', label: 'LinkedIn', icon: 'linkedin', color: '#0077B5' },
                { id: 'twitter',  label: 'Twitter/X', icon: 'twitter', color: '#1DA1F2' },
                { id: 'email',    label: 'Email',    icon: 'email-outline', color: '#0e4e96' },
              ].map(s => (
                <TouchableOpacity key={s.id} style={styles.shareOption} onPress={() => handleShareTo(s.id)}>
                  <View style={[styles.shareIconWrap, { backgroundColor: s.color + '22' }]}>
                    <MaterialCommunityIcons name={s.icon} size={26} color={s.color} />
                  </View>
                  <Text style={styles.shareLabel}>{s.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity style={styles.shareCancelBtn} onPress={() => setShowShare(false)}>
              <Text style={styles.shareCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Reading settings modal */}
      <Modal visible={showSettings} transparent animationType="slide" onRequestClose={() => setShowSettings(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowSettings(false)}>
          <View style={styles.settingsSheet}>
            <Text style={styles.settingsTitle}>Reading Settings</Text>

            <Text style={styles.settingsLabel}>Text Size</Text>
            <View style={styles.fontSizeRow}>
              {FONT_SIZES.map((_, i) => (
                <TouchableOpacity key={i} style={[styles.fontSizeBtn, fontSize === i && styles.fontSizeBtnActive]}
                  onPress={() => setFontSize(i)}>
                  <Text style={[styles.fontSizeBtnText, fontSize === i && styles.fontSizeBtnTextActive, { fontSize: FONT_SIZES[i] - 2 }]}>
                    {FONT_LABELS[i]}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.settingsLabel}>Theme</Text>
            <View style={styles.themeRow}>
              <TouchableOpacity style={[styles.themeBtn, !darkMode && styles.themeBtnActive]} onPress={() => setDarkMode(false)}>
                <MaterialCommunityIcons name="white-balance-sunny" size={20} color={!darkMode ? '#0e4e96' : '#9ca3af'} />
                <Text style={[styles.themeBtnText, !darkMode && styles.themeBtnTextActive]}>Light</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.themeBtn, darkMode && styles.themeBtnActive]} onPress={() => setDarkMode(true)}>
                <MaterialCommunityIcons name="moon-waning-crescent" size={20} color={darkMode ? '#0e4e96' : '#9ca3af'} />
                <Text style={[styles.themeBtnText, darkMode && styles.themeBtnTextActive]}>Dark</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Comments modal */}
      <Modal visible={showComments} transparent animationType="slide" onRequestClose={() => setShowComments(false)}>
        <View style={styles.commentsModal}>
          <View style={styles.commentsHeader}>
            <Text style={styles.commentsTitle}>Comments ({comments.length})</Text>
            <TouchableOpacity onPress={() => setShowComments(false)}>
              <MaterialIcons name="close" size={24} color="#6b7280" />
            </TouchableOpacity>
          </View>

          {/* Comment input */}
          <View style={styles.commentInput}>
            <TextInput
              style={styles.commentField}
              placeholder="Add a comment..."
              value={commentText}
              onChangeText={setCommentText}
              multiline
              maxLength={500}
            />
            <TouchableOpacity style={styles.commentSubmitBtn} onPress={submitComment} disabled={!commentText.trim()}>
              <MaterialIcons name="send" size={20} color="#fff" />
            </TouchableOpacity>
          </View>

          <ScrollView style={{ flex: 1 }}>
            {comments.map(c => (
              <View key={c.id} style={styles.commentItem}>
                <View style={styles.commentAvatar}>
                  <Text style={styles.commentAvatarText}>{c.avatar}</Text>
                </View>
                <View style={styles.commentBody}>
                  <View style={styles.commentMeta}>
                    <Text style={styles.commentAuthor}>{c.author}</Text>
                    <Text style={styles.commentTime}>{c.time}</Text>
                  </View>
                  <Text style={styles.commentText}>{c.text}</Text>
                  <TouchableOpacity style={styles.commentLike} onPress={() => toggleCommentLike(c.id)}>
                    <MaterialCommunityIcons name={c.liked ? 'heart' : 'heart-outline'} size={14} color={c.liked ? '#ef4444' : '#9ca3af'} />
                    <Text style={styles.commentLikeText}>{c.likes}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
            <View style={{ height: 30 }} />
          </ScrollView>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container:        { flex: 1 },
  progressBar:      { height: 3, backgroundColor: '#e5e7eb' },
  progressFill:     { height: '100%', backgroundColor: '#0e4e96' },
  header:           { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1 },
  headerBtn:        { padding: 6 },
  headerReadInfo:   { flexDirection: 'row', alignItems: 'center', gap: 4, flex: 1, justifyContent: 'center' },
  headerReadTime:   { fontSize: 11, color: '#9ca3af' },
  headerActions:    { flexDirection: 'row' },
  previewBadge:     { backgroundColor: '#f59e0b', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  previewText:      { color: '#fff', fontSize: 11, fontWeight: '700' },
  breakingBanner:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#dc2626', paddingVertical: 8, gap: 6 },
  breakingText:     { color: '#fff', fontSize: 12, fontWeight: '900', letterSpacing: 1 },
  articleHeader:    { padding: 20 },
  metaRow:          { flexDirection: 'row', alignItems: 'center', marginBottom: 14, gap: 8 },
  categoryBadge:    { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  categoryText:     { fontSize: 12, fontWeight: '600' },
  priorityDot:      { width: 8, height: 8, borderRadius: 4 },
  viewsRow:         { flexDirection: 'row', alignItems: 'center', gap: 3, marginLeft: 'auto' },
  viewsText:        { fontSize: 11, color: '#9ca3af' },
  articleTitle:     { fontWeight: '800', lineHeight: 32, marginBottom: 16 },
  authorRow:        { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  authorAvatar:     { width: 34, height: 34, borderRadius: 17, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  authorAvatarText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  authorName:       { fontSize: 14, fontWeight: '600' },
  authorSource:     { fontSize: 12, color: '#9ca3af' },
  dateRow:          { flexDirection: 'row', alignItems: 'center', gap: 5 },
  dateText:         { fontSize: 12, color: '#9ca3af' },
  imageContainer:   { marginHorizontal: 20, marginBottom: 20, borderRadius: 14, overflow: 'hidden' },
  heroImage:        { width: '100%', height: 220 },
  articleBody:      { paddingHorizontal: 20, paddingBottom: 20 },
  snippetText:      { fontWeight: '500', lineHeight: 26, marginBottom: 20, fontStyle: 'italic', borderLeftWidth: 3, borderLeftColor: '#0e4e96', paddingLeft: 12 },
  bodyFallback:     { lineHeight: 26 },
  tagsSection:      { paddingHorizontal: 20, marginBottom: 20 },
  tagsTitle:        { fontSize: 14, fontWeight: '600', marginBottom: 10 },
  tagsRow:          { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  tag:              { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14 },
  tagText:          { fontSize: 12 },
  // AI card
  aiCard:           { margin: 16, borderRadius: 14, padding: 16 },
  aiCardHeader:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  aiIconRow:        { flexDirection: 'row', alignItems: 'center', gap: 6 },
  aiCardTitle:      { fontSize: 15, fontWeight: '700' },
  aiGenerateBtn:    { backgroundColor: '#0e4e96', paddingHorizontal: 14, paddingVertical: 6, borderRadius: 8 },
  aiGenerateText:   { color: '#fff', fontSize: 13, fontWeight: '600' },
  aiLoading:        { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10 },
  aiLoadingText:    { color: '#6b7280', fontSize: 13 },
  aiBody:           {},
  aiScoreRow:       { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 },
  aiLabel:          { fontSize: 12, fontWeight: '600', flex: 1 },
  aiScoreBar:       { flex: 2, height: 6, backgroundColor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' },
  aiScoreFill:      { height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 },
  aiScore:          { fontSize: 12, fontWeight: '700', color: '#0e4e96' },
  aiSectionLabel:   { fontSize: 12, fontWeight: '700', marginBottom: 4, marginTop: 10, textTransform: 'uppercase', color: '#6b7280' },
  aiText:           { fontSize: 13, lineHeight: 20 },
  aiSkillRow:       { flexDirection: 'row', alignItems: 'flex-start', gap: 6, marginBottom: 4 },
  aiSkillText:      { fontSize: 13, flex: 1 },
  aiRefreshBtn:     { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 12, alignSelf: 'flex-end' },
  aiRefreshText:    { fontSize: 12, color: '#0e4e96' },
  aiHint:           { fontSize: 12, marginTop: 4 },
  // Engagement
  engagementBar:    { flexDirection: 'row', borderTopWidth: 1, borderBottomWidth: 1, paddingVertical: 12, paddingHorizontal: 20, gap: 12 },
  engBtn:           { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: '#e5e7eb', gap: 6 },
  engBtnLiked:      { backgroundColor: '#ef4444', borderColor: '#ef4444' },
  engBtnText:       { fontSize: 13, color: '#0e4e96', fontWeight: '500' },
  engBtnTextLiked:  { color: '#fff' },
  // Reading stats
  readingStats:     { flexDirection: 'row', justifyContent: 'space-around', margin: 16, borderRadius: 12, padding: 14 },
  readingStatItem:  { flexDirection: 'row', alignItems: 'center', gap: 5 },
  readingStatText:  { fontSize: 11, color: '#9ca3af' },
  // Related
  relatedSection:   { paddingHorizontal: 16, paddingBottom: 20 },
  relatedTitle:     { fontSize: 17, fontWeight: '700', marginBottom: 14 },
  relatedCard:      { flexDirection: 'row', alignItems: 'center', borderRadius: 12, padding: 12, marginBottom: 10, gap: 10 },
  relatedThumb:     { width: 64, height: 64, borderRadius: 8 },
  relatedThumbPlaceholder: { backgroundColor: '#f3f4f6', justifyContent: 'center', alignItems: 'center' },
  relatedContent:   { flex: 1 },
  relatedCategory:  { fontSize: 10, fontWeight: '600', textTransform: 'uppercase', marginBottom: 3 },
  relatedArticleTitle: { fontSize: 13, fontWeight: '600', lineHeight: 18, marginBottom: 4 },
  relatedMeta:      { fontSize: 11, color: '#9ca3af' },
  // Share modal
  modalOverlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  shareSheet:       { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24 },
  shareTitle:       { fontSize: 17, fontWeight: '700', color: '#1f2937', marginBottom: 20 },
  shareGrid:        { flexDirection: 'row', justifyContent: 'space-around', marginBottom: 20 },
  shareOption:      { alignItems: 'center', gap: 8 },
  shareIconWrap:    { width: 56, height: 56, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  shareLabel:       { fontSize: 12, color: '#374151', fontWeight: '500' },
  shareCancelBtn:   { alignItems: 'center', paddingVertical: 14, backgroundColor: '#f3f4f6', borderRadius: 12 },
  shareCancelText:  { fontSize: 15, color: '#6b7280', fontWeight: '600' },
  // Settings modal
  settingsSheet:    { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24 },
  settingsTitle:    { fontSize: 17, fontWeight: '700', color: '#1f2937', marginBottom: 20 },
  settingsLabel:    { fontSize: 13, fontWeight: '600', color: '#6b7280', marginBottom: 10, textTransform: 'uppercase' },
  fontSizeRow:      { flexDirection: 'row', gap: 8, marginBottom: 24 },
  fontSizeBtn:      { flex: 1, paddingVertical: 10, backgroundColor: '#f3f4f6', borderRadius: 10, alignItems: 'center' },
  fontSizeBtnActive:{ backgroundColor: '#0e4e96' },
  fontSizeBtnText:  { color: '#6b7280', fontWeight: '600' },
  fontSizeBtnTextActive: { color: '#fff' },
  themeRow:         { flexDirection: 'row', gap: 12 },
  themeBtn:         { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, backgroundColor: '#f3f4f6', borderRadius: 12, gap: 6 },
  themeBtnActive:   { backgroundColor: '#e8f0fe', borderWidth: 1.5, borderColor: '#0e4e96' },
  themeBtnText:     { fontSize: 14, color: '#6b7280', fontWeight: '500' },
  themeBtnTextActive: { color: '#0e4e96', fontWeight: '700' },
  // Comments modal
  commentsModal:    { flex: 1, backgroundColor: '#fff', marginTop: 60, borderTopLeftRadius: 24, borderTopRightRadius: 24 },
  commentsHeader:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  commentsTitle:    { fontSize: 17, fontWeight: '700', color: '#1f2937' },
  commentInput:     { flexDirection: 'row', padding: 16, gap: 10, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  commentField:     { flex: 1, backgroundColor: '#f9fafb', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, minHeight: 44, maxHeight: 100, borderWidth: 1, borderColor: '#e5e7eb' },
  commentSubmitBtn: { width: 44, height: 44, borderRadius: 22, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  commentItem:      { flexDirection: 'row', padding: 16, borderBottomWidth: 1, borderBottomColor: '#f9fafb', gap: 10 },
  commentAvatar:    { width: 34, height: 34, borderRadius: 17, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  commentAvatarText:{ color: '#fff', fontWeight: '700', fontSize: 14 },
  commentBody:      { flex: 1 },
  commentMeta:      { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  commentAuthor:    { fontSize: 13, fontWeight: '600', color: '#1f2937' },
  commentTime:      { fontSize: 11, color: '#9ca3af' },
  commentText:      { fontSize: 13, color: '#374151', lineHeight: 19 },
  commentLike:      { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 6 },
  commentLikeText:  { fontSize: 11, color: '#9ca3af' },
  // Error
  errorContainer:   { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  errorTitle:       { fontSize: 22, fontWeight: '700', color: '#1f2937', marginTop: 16, marginBottom: 8 },
  errorMessage:     { fontSize: 15, color: '#6b7280', textAlign: 'center', marginBottom: 24 },
  errorButton:      { backgroundColor: '#0e4e96', paddingHorizontal: 28, paddingVertical: 12, borderRadius: 10 },
  errorButtonText:  { color: '#fff', fontSize: 15, fontWeight: '600' },
});
